/* Data tables — every table in the database, read-only, for an admin to
 * look at what the loaders actually wrote: the rows, the schema around
 * them, and a profile of each column for quick data QA.
 *
 * The view lives in the URL (`t`, `tab`, `sort`, `dir`, `q`, `f`, `page`,
 * `n`), so a look at "machine_shift_log where count = 40s, sorted by date"
 * is a link someone can be sent, and the back button walks back through
 * the clicks. A click on a cell adds its value as a filter; a foreign-key
 * cell is a link into the table it points at. */

import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { MAIN_STYLE, Subline } from '../App'
import { Blueprint, Empty, ErrorBox, Loading, PanelHead, Segmented } from '../components/ui'
import { useAsync } from '../lib/useAsync'
import { num } from '../lib/format'
import { useCan } from '../lib/auth'
import {
  admin, parseFilter,
  type Browse, type Check, type Column, type Dir, type Filter, type ProfileColumn, type TablesIndex,
} from '../lib/api_admin'

type Tab = 'rows' | 'columns' | 'qa'
const TABS: { value: Tab; label: string }[] = [
  { value: 'rows', label: 'Rows' }, { value: 'columns', label: 'Columns' }, { value: 'qa', label: 'QA' },
]
const PAGE_SIZES = [50, 100, 250, 500]
const DEFAULT_SIZE = 100
const TRUNCATE_AT = 60

const R: CSSProperties = { textAlign: 'right' }
const NOWRAP: CSSProperties = { whiteSpace: 'nowrap' }
const small: CSSProperties = { fontSize: 12, padding: '5px 10px' }

// ------------------------------------------------------------------ values
const NUMERIC = /int|real|num|float|double|dec/i
const isNumericType = (t: string) => NUMERIC.test(t)
/** An id is a label, not a quantity: no digit grouping. */
const isIdLike = (c: Column) => c.primary_key || !!c.foreign_key || c.name === 'id' || /_id$/.test(c.name)

const fmtNumber = (v: number, id: boolean) =>
  (id ? String(v) : v.toLocaleString('en-IN', { maximumFractionDigits: 2 }))

/** The raw value as a filter sends it back. */
const asFilterValue = (v: unknown): string => (typeof v === 'object' ? JSON.stringify(v) : String(v))

/** A cell's text and whether it was cut short. */
function cellText(v: unknown, id: boolean): { text: string; full: string | null } {
  if (typeof v === 'number') return { text: fmtNumber(v, id), full: null }
  if (typeof v === 'boolean') return { text: v ? '✓' : '–', full: null }
  const s = typeof v === 'string' ? v : JSON.stringify(v)
  return s.length > TRUNCATE_AT ? { text: s.slice(0, TRUNCATE_AT - 1) + '…', full: s } : { text: s, full: null }
}

/** A profile's min/max/top value, short. */
const brief = (v: unknown): string => {
  if (v === null || v === undefined) return '–'
  if (typeof v === 'number') return v.toLocaleString('en-IN', { maximumFractionDigits: 2 })
  if (typeof v === 'boolean') return v ? 'true' : 'false'
  const s = typeof v === 'string' ? (v === '' ? '(blank)' : v) : JSON.stringify(v)
  return s.length > 40 ? s.slice(0, 39) + '…' : s
}

const fkTarget = (fk: string): { table: string; column: string } => {
  const i = fk.lastIndexOf('.')
  return i < 0 ? { table: fk, column: 'id' } : { table: fk.slice(0, i), column: fk.slice(i + 1) }
}

/** The link that opens `table` filtered to `column = value`. */
const tableHref = (table: string, filter?: Filter, tab: Tab = 'rows') => {
  const p = new URLSearchParams({ t: table })
  if (tab !== 'rows') p.set('tab', tab)
  if (filter) p.append('f', `${filter.column}:${filter.value}`)
  return `/tables?${p.toString()}`
}

// -------------------------------------------------------------------- page
export default function Tables() {
  const manage = useCan('manage')
  if (!manage) {
    return (
      <main style={MAIN_STYLE}>
        <Subline>The database, table by table</Subline>
        <Empty title="For admins">
          The tables behind every page — rows, columns and a data-quality profile of each — are
          an admin's view. Ask an admin if you need to see what was loaded.
        </Empty>
      </main>
    )
  }
  return <Console />
}

/** The parts of the URL the page reads, parsed once per change. */
interface View {
  t: string | null
  tab: Tab
  sort: string | null
  dir: Dir
  q: string
  filters: Filter[]
  page: number
  size: number
}

function Console() {
  const [params, setParams] = useSearchParams()
  const view = useMemo<View>(() => {
    const tab = params.get('tab')
    const size = Number(params.get('n'))
    return {
      t: params.get('t'),
      tab: tab === 'columns' || tab === 'qa' ? tab : 'rows',
      sort: params.get('sort'),
      dir: params.get('dir') === 'desc' ? 'desc' : 'asc',
      q: params.get('q') ?? '',
      filters: params.getAll('f').map(parseFilter).filter((f): f is Filter => f !== null),
      page: Math.max(1, Number(params.get('page')) || 1),
      size: PAGE_SIZES.includes(size) ? size : DEFAULT_SIZE,
    }
  }, [params])

  /** Rewrite part of the URL; `null` drops a key, an array repeats it.
   *  Anything the page does not own (`full`) rides along untouched. */
  const update = (patch: Record<string, string | string[] | null | undefined>, replace = false) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) {
      next.delete(k)
      if (Array.isArray(v)) v.forEach((x) => next.append(k, x))
      else if (v != null && v !== '') next.set(k, v)
    }
    setParams(next, { replace })
  }

  // The index — re-read when the table changes so the row counts beside the
  // names stay right without polling.
  const index = useAsync(() => admin.tables(), [view.t])
  const all = useMemo(() => index.data?.groups.flatMap((g) => g.tables) ?? [], [index.data])
  const chosen = view.t ?? (all.find((x) => x.rows > 0) ?? all[0])?.name ?? null

  const nTables = all.length
  return (
    <>
      <Subline>
        {index.data
          ? `${num(nTables)} table${nTables === 1 ? '' : 's'} · ${num(index.data.total_rows)} rows · read-only`
          : 'The database, table by table'}
      </Subline>
      <main style={MAIN_STYLE}>
        {index.error && !index.data ? <ErrorBox error={index.error} onRetry={index.reload} /> : null}
        {index.loading && !index.data ? <Loading what="the tables" /> : null}
        {index.data ? (
          <div className="tables-layout">
            <TableList index={index.data} chosen={chosen} tab={view.tab} />
            {chosen ? (
              <TablePanel key={chosen} name={chosen} view={view} update={update} />
            ) : (
              <Empty title="No tables">The database has no tables to show.</Empty>
            )}
          </div>
        ) : null}
      </main>
    </>
  )
}

// -------------------------------------------------------------------- list
function TableList({ index, chosen, tab }: { index: TablesIndex; chosen: string | null; tab: Tab }) {
  const [needle, setNeedle] = useState('')
  const n = needle.trim().toLowerCase()
  const groups = index.groups
    .map((g) => ({ ...g, tables: n ? g.tables.filter((t) => t.name.toLowerCase().includes(n)) : g.tables }))
    .filter((g) => g.tables.length > 0)

  return (
    <Blueprint className="sheet" style={{ padding: '18px 16px 16px', position: 'sticky', top: 56 }}>
      <PanelHead title="Tables" hint={`${num(index.total_rows)} rows`} />
      <input className="input" type="search" placeholder="Filter tables" value={needle}
        autoComplete="off" spellCheck={false} aria-label="Filter tables"
        style={{ width: '100%', boxSizing: 'border-box', marginTop: 12, fontSize: 13, padding: '6px 9px' }}
        onChange={(e) => setNeedle(e.target.value)} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14, maxHeight: '70vh', overflowY: 'auto' }}>
        {groups.length === 0 ? (
          <div className="muted" style={{ fontSize: 13 }}>No table is called that.</div>
        ) : null}
        {groups.map((g) => (
          <div key={g.key}>
            <div className="eyebrow" style={{ padding: '0 8px' }}>{g.title}</div>
            {g.sub ? <div className="muted" style={{ fontSize: 11, padding: '1px 8px 4px' }}>{g.sub}</div> : null}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {g.tables.map((t) => (
                <Link key={t.name} to={tableHref(t.name, undefined, tab)}
                  className={`tables-row ${t.name === chosen ? 'on' : ''}`}
                  title={t.doc ?? undefined}>
                  <span className="truncate" style={{ opacity: t.rows ? 1 : 0.55 }}>{t.name}</span>
                  <span className="num" style={{ color: 'var(--color-neutral-600)', flex: 'none' }}>{num(t.rows)}</span>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Blueprint>
  )
}

// ------------------------------------------------------------------- panel
function TablePanel({ name, view, update }: {
  name: string
  view: View
  update: (patch: Record<string, string | string[] | null | undefined>, replace?: boolean) => void
}) {
  const { tab, sort, dir, q, filters, page, size } = view
  const query = {
    limit: size, offset: (page - 1) * size,
    sort, dir: sort ? dir : null, q: q || null, filters,
  }
  // One string the loader re-runs on, rather than a dep per field.
  const key = JSON.stringify(query)
  const browse = useAsync(() => admin.browse(name, query), [name, key])
  const profile = useAsync(
    () => (tab === 'qa' ? admin.profile(name) : Promise.resolve(null)),
    [name, tab === 'qa'],
  )

  const data = browse.data
  const total = data?.total ?? null
  // A page past the end — the filter changed under it — is walked back.
  useEffect(() => {
    if (total !== null && page > 1 && (page - 1) * size >= total) {
      update({ page: String(Math.max(1, Math.ceil(total / size))) }, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, page, size])

  const setTab = (t: Tab) => update({ tab: t === 'rows' ? null : t })
  const addFilter = (f: Filter) => {
    if (filters.some((x) => x.column === f.column && x.value === f.value)) return
    update({ f: [...filters, f].map((x) => `${x.column}:${x.value}`), page: null })
  }
  const dropFilter = (i: number) =>
    update({ f: filters.filter((_, j) => j !== i).map((x) => `${x.column}:${x.value}`), page: null })
  const toggleSort = (col: string) => {
    if (sort !== col) update({ sort: col, dir: null, page: null })
    else if (dir === 'asc') update({ sort: col, dir: 'desc', page: null })
    else update({ sort: null, dir: null, page: null })
  }

  const header = (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: 22, margin: 0 }}>{name}</h2>
          {total !== null ? (
            <span className="num" style={{ fontSize: 12, color: 'var(--color-neutral-600)' }}>
              {num(total)} row{total === 1 ? '' : 's'}
              {filters.length || q ? ' matching' : ''}
            </span>
          ) : null}
          <span className="tag tag-neutral">read-only</span>
        </div>
        {data?.doc ? (
          <div className="pretty" style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 4, maxWidth: '80ch' }}>
            {data.doc}
          </div>
        ) : null}
      </div>
      <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Segmented value={tab} options={TABS} onChange={setTab} />
        <a className="btn btn-secondary" href={admin.exportUrl(name, query)} download={`${name}.csv`}
          title="The rows as filtered and sorted here, every page">
          Export CSV
        </a>
      </div>
    </div>
  )

  return (
    <Blueprint className="sheet" style={{ padding: '22px 24px', minWidth: 0 }}>
      {header}
      {browse.error ? <div style={{ marginTop: 14 }}><ErrorBox error={browse.error} onRetry={browse.reload} /></div> : null}
      {browse.loading && !data ? <Loading what={name} /> : null}
      {data && tab === 'rows' ? (
        <RowsTab data={data} view={view} loading={browse.loading}
          onSearch={(s) => update({ q: s || null, page: null })}
          onSize={(n) => update({ n: n === DEFAULT_SIZE ? null : String(n), page: null })}
          onPage={(p) => update({ page: p === 1 ? null : String(p) })}
          onSort={toggleSort} onFilter={addFilter} onDropFilter={dropFilter}
          onClear={() => update({ q: null, f: null, sort: null, dir: null, page: null })} />
      ) : null}
      {data && tab === 'columns' ? <ColumnsTab data={data} /> : null}
      {tab === 'qa' ? (
        <>
          {profile.error ? <div style={{ marginTop: 14 }}><ErrorBox error={profile.error} onRetry={profile.reload} /></div> : null}
          {profile.loading && !profile.data ? <Loading what={`the profile of ${name}`} /> : null}
          {profile.data ? <QaTab checks={profile.data.checks} columns={profile.data.columns} rows={profile.data.rows}
            onFilter={(f) => { addFilter(f); setTab('rows') }} /> : null}
        </>
      ) : null}
    </Blueprint>
  )
}

// -------------------------------------------------------------------- rows
function RowsTab({ data, view, loading, onSearch, onSize, onPage, onSort, onFilter, onDropFilter, onClear }: {
  data: Browse
  view: View
  loading: boolean
  onSearch: (q: string) => void
  onSize: (n: number) => void
  onPage: (p: number) => void
  onSort: (col: string) => void
  onFilter: (f: Filter) => void
  onDropFilter: (i: number) => void
  onClear: () => void
}) {
  const { q, filters, sort, dir, page, size } = view
  const { columns, rows, total } = data

  // The search box is typed into freely and reaches the URL after a pause;
  // a URL change from outside (back button) takes the box with it.
  const [draft, setDraft] = useState(q)
  useEffect(() => { setDraft(q) }, [q])
  useEffect(() => {
    if (draft === q) return
    const id = window.setTimeout(() => onSearch(draft.trim()), 300)
    return () => window.clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft])

  const first = total === 0 ? 0 : (page - 1) * size + 1
  const last = Math.min(total, (page - 1) * size + rows.length)
  const pages = Math.max(1, Math.ceil(total / size))
  const byName = useMemo(() => Object.fromEntries(columns.map((c) => [c.name, c])), [columns])
  const numeric = useMemo(() => {
    const out: Record<string, boolean> = {}
    for (const c of columns) {
      const sample = rows.find((r) => r[c.name] !== null && r[c.name] !== undefined)?.[c.name]
      out[c.name] = sample !== undefined ? typeof sample === 'number' : isNumericType(c.type)
    }
    return out
  }, [columns, rows])
  const narrowed = Boolean(q || filters.length || sort)

  return (
    <>
      <div className="no-print" style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 14,
      }}>
        <input className="input" type="search" placeholder="Search text columns" value={draft}
          autoComplete="off" spellCheck={false} aria-label="Search text columns"
          style={{ fontSize: 13, padding: '6px 9px', width: 220 }}
          onChange={(e) => setDraft(e.target.value)} />
        {filters.map((f, i) => (
          <span key={`${f.column}:${f.value}:${i}`} className="tag tag-neutral" style={{ gap: 6, paddingRight: 6 }}>
            <span className="num">{f.column} = {f.value === '' ? '(blank)' : f.value}</span>
            <button type="button" aria-label={`Remove filter ${f.column}`} onClick={() => onDropFilter(i)} style={{
              font: 'inherit', background: 'none', border: 0, padding: '0 2px', cursor: 'pointer', color: 'inherit', lineHeight: 1,
            }}>×</button>
          </span>
        ))}
        {narrowed ? (
          <button type="button" className="btn btn-ghost" style={small} onClick={onClear}>Clear</button>
        ) : null}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span className="axis-title">Per page</span>
          <select value={size} style={{ fontSize: 13, padding: '5px 8px' }} aria-label="Rows per page"
            onChange={(e) => onSize(Number(e.target.value))}>
            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <span className="num" style={{ fontSize: 12, color: 'var(--color-neutral-600)', whiteSpace: 'nowrap' }}>
            {total === 0 ? 'no rows' : `rows ${num(first)}–${num(last)} of ${num(total)}`}
            {loading ? ' …' : ''}
          </span>
          <div className="seg">
            <button type="button" className="btn btn-secondary" style={small} disabled={page <= 1}
              onClick={() => onPage(page - 1)} aria-label="Previous page">‹ Prev</button>
            <button type="button" className="btn btn-secondary" style={small} disabled={page >= pages}
              onClick={() => onPage(page + 1)} aria-label="Next page">Next ›</button>
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--color-neutral-600)', padding: '18px 0 6px' }}>
          {narrowed || q ? 'No rows match.' : 'The table is empty.'}
        </div>
      ) : (
        <div className="scroll-x" style={{ marginTop: 14, opacity: loading ? 0.6 : 1, transition: 'opacity 120ms ease' }}>
          <table className="table" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                {columns.map((c) => {
                  const on = sort === c.name
                  return (
                    <th key={c.name} className="sortable" style={numeric[c.name] ? { ...R, ...NOWRAP } : NOWRAP}
                      onClick={() => onSort(c.name)} aria-sort={on ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                      title={`${c.type}${c.nullable ? '' : ' · not null'}${c.primary_key ? ' · primary key' : ''}${c.foreign_key ? ` · → ${c.foreign_key}` : ''}\nClick to sort`}>
                      {c.name}
                      <span aria-hidden="true" style={{ marginLeft: 4, opacity: on ? 1 : 0.25, fontSize: 9 }}>
                        {on ? (dir === 'asc' ? '▲' : '▼') : '▲▼'}
                      </span>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  {columns.map((c) => (
                    <Cell key={c.name} column={byName[c.name]} value={r[c.name]} numeric={numeric[c.name]} onFilter={onFilter} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

function Cell({ column, value, numeric, onFilter }: {
  column: Column; value: unknown; numeric: boolean; onFilter: (f: Filter) => void
}) {
  const style: CSSProperties = numeric ? { ...R, ...NOWRAP } : NOWRAP
  if (value === null || value === undefined) {
    return <td className={numeric ? 'num muted' : 'muted'} style={style}>–</td>
  }
  const { text, full } = cellText(value, isIdLike(column))
  const raw = asFilterValue(value)
  if (column.foreign_key) {
    const target = fkTarget(column.foreign_key)
    return (
      <td className="num" style={style}>
        <Link to={tableHref(target.table, { column: target.column, value: raw })}
          title={`Open ${target.table} where ${target.column} = ${raw}`}>{text}</Link>
      </td>
    )
  }
  return (
    <td className={numeric ? 'num' : undefined} style={style}>
      <button type="button" className="cell-filter" onClick={() => onFilter({ column: column.name, value: raw })}
        title={full ?? `Only rows where ${column.name} = ${raw}`}>{text}</button>
    </td>
  )
}

// ----------------------------------------------------------------- columns
function ColumnsTab({ data }: { data: Browse }) {
  const { columns, points_at, pointed_at_by } = data
  const listStyle: CSSProperties = { margin: '6px 0 0', paddingLeft: 18, fontSize: 13, lineHeight: 1.6 }
  return (
    <>
      <div className="scroll-x" style={{ marginTop: 14 }}>
        <table className="table" style={{ fontSize: 13 }}>
          <thead>
            <tr><th>Column</th><th>Type</th><th>Nullable</th><th>Key</th><th>Foreign key</th></tr>
          </thead>
          <tbody>
            {columns.map((c) => {
              const fk = c.foreign_key ? fkTarget(c.foreign_key) : null
              return (
                <tr key={c.name}>
                  <td style={{ fontWeight: 600, ...NOWRAP }}>{c.name}</td>
                  <td className="num" style={NOWRAP}>{c.type || '–'}</td>
                  <td className="muted">{c.nullable ? 'yes' : 'no'}</td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    {c.primary_key ? <span className="tag tag-accent">PK</span> : null}
                    {c.unique && !c.primary_key ? <span className="tag tag-outline">unique</span> : null}
                  </td>
                  <td style={NOWRAP}>
                    {fk ? <Link to={tableHref(fk.table)} title={`Open ${fk.table}`}>{fk.table}.{fk.column}</Link> : <span className="muted">–</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24, marginTop: 20 }}>
        <div>
          <div className="eyebrow">Points at</div>
          {points_at.length ? (
            <ul style={listStyle}>
              {points_at.map((p) => (
                <li key={`${p.column}-${p.table}`}>
                  <span className="num">{p.column}</span>
                  <span className="muted"> → </span>
                  <Link to={tableHref(p.table)}>{p.table}</Link>
                  <span className="muted num">.{p.to}</span>
                </li>
              ))}
            </ul>
          ) : <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>No foreign keys.</div>}
        </div>
        <div>
          <div className="eyebrow">Pointed at by</div>
          {pointed_at_by.length ? (
            <ul style={listStyle}>
              {pointed_at_by.map((p) => (
                <li key={`${p.table}-${p.column}`}>
                  <Link to={tableHref(p.table)}>{p.table}</Link>
                  <span className="muted num">.{p.column}</span>
                </li>
              ))}
            </ul>
          ) : <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>Nothing points here.</div>}
        </div>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------- qa
const DOT: Record<Check['status'], string> = {
  ok: 'var(--color-good)',
  warn: 'var(--c-shB)',
  info: 'var(--color-neutral-500)',
}

function QaTab({ checks, columns, rows, onFilter }: {
  checks: Check[]; columns: ProfileColumn[]; rows: number; onFilter: (f: Filter) => void
}) {
  return (
    <>
      <div style={{ marginTop: 16 }}>
        <div className="eyebrow">Checks</div>
        {checks.length === 0 ? (
          <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>Nothing to check on this table.</div>
        ) : (
          <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {checks.map((c, i) => (
              <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13 }}>
                <span className="qa-dot" style={{ background: DOT[c.status] ?? DOT.info }} title={c.status} />
                <div>
                  <span style={{ fontWeight: 600 }}>{c.label}</span>
                  {c.detail ? <span className="muted"> — {c.detail}</span> : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="scroll-x" style={{ marginTop: 20 }}>
        <table className="table" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th>Column</th><th>Type</th><th style={R}>Nulls</th><th style={R}>Distinct</th>
              <th>Min</th><th>Max</th><th style={R}>Blank</th><th>Top values</th>
            </tr>
          </thead>
          <tbody>
            {columns.map((c) => (
              <tr key={c.name}>
                <td style={{ fontWeight: 600, ...NOWRAP }}>{c.name}</td>
                <td className="num muted" style={NOWRAP}>{c.type || '–'}</td>
                <td className="num" style={{ ...R, ...NOWRAP, color: c.nulls && rows ? undefined : 'var(--color-neutral-600)' }}>
                  {num(c.nulls)}
                  {rows ? <span className="muted" style={{ fontSize: 11 }}> · {c.null_pct.toFixed(c.null_pct && c.null_pct < 1 ? 1 : 0)}%</span> : null}
                </td>
                <td className="num" style={R}>{num(c.distinct)}</td>
                <td className="num" style={NOWRAP} title={c.min == null ? undefined : String(c.min)}>{brief(c.min)}</td>
                <td className="num" style={NOWRAP} title={c.max == null ? undefined : String(c.max)}>{brief(c.max)}</td>
                <td className="num" style={{ ...R, color: c.blank ? 'var(--color-warn)' : 'var(--color-neutral-600)' }}>{num(c.blank)}</td>
                <td>
                  {c.top && c.top.length ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {c.top.map((t, i) => (
                        <TopChip key={i} value={t.value} n={t.n}
                          onClick={t.value === null || t.value === undefined ? undefined
                            : () => onFilter({ column: c.name, value: asFilterValue(t.value) })} />
                      ))}
                    </div>
                  ) : <span className="muted">–</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pretty" style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 12, maxWidth: '90ch' }}>
        Nulls are missing values; blanks are empty strings in a text column, which a loader
        usually meant as missing. A top value opens the rows that carry it.
      </div>
    </>
  )
}

function TopChip({ value, n, onClick }: { value: unknown; n: number; onClick?: () => void }) {
  const label: ReactNode = <>{brief(value)} <span style={{ opacity: 0.6 }}>×{num(n)}</span></>
  const full = value == null ? 'null' : String(value)
  return onClick ? (
    <button type="button" className="tag tag-neutral num" onClick={onClick}
      title={`Only rows where this column = ${full}`}
      style={{ border: 0, cursor: 'pointer', font: 'inherit', fontSize: 11 }}>{label}</button>
  ) : (
    <span className="tag tag-neutral num" title={full}>{label}</span>
  )
}
